import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../Shared/ui/button';

const Landing: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header with Login Button */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-blue-600">Numbie Learning</h1>
          </div>
          <Button onClick={() => navigate('/login')} className="bg-blue-600 hover:bg-blue-700">
            Login
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <div className="container mx-auto px-4 pt-16 pb-12">
        <div className="text-center mb-16 max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            AI-Powered Math Learning
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 mb-6">
            Revolutionary educational platform that combines cutting-edge AI with proven learning
            methodologies
          </p>
          <p className="text-lg text-gray-500 mb-8">
            Personalized math education that adapts to each student's pace and learning style
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/signup')}
              className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-6"
            >
              Get Started Free
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/login')}
              className="text-lg px-8 py-6"
            >
              Sign In
            </Button>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-16 max-w-4xl mx-auto">
          <div className="text-center mb-6">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">How Numbie Works</h3>
            <p className="text-lg text-gray-600">
              AI-powered adaptive learning that grows with your child
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <div className="text-2xl">🧠</div>
              <div>
                <h4 className="font-semibold mb-1">Tracks Understanding</h4>
                <p className="text-gray-600 text-sm">
                  Monitors each student's mastery of different math concepts
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-2xl">🔮</div>
              <div>
                <h4 className="font-semibold mb-1">Early Detection</h4>
                <p className="text-gray-600 text-sm">
                  Identifies learning gaps before they become problems
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-2xl">⚡</div>
              <div>
                <h4 className="font-semibold mb-1">Smart Adaptation</h4>
                <p className="text-gray-600 text-sm">
                  Automatically adjusts difficulty as students progress
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-2xl">🎯</div>
              <div>
                <h4 className="font-semibold mb-1">Proven Results</h4>
                <p className="text-gray-600 text-sm">
                  Data-driven approach to maximize learning outcomes
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="mb-16 max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Choose Numbie?</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-4xl mb-4">🎯</div>
              <h4 className="font-semibold text-xl mb-3">Personalized Learning</h4>
              <p className="text-gray-600">
                AI-powered content that adapts to each student's skill level with dynamic difficulty
                adjustment
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-4xl mb-4">📊</div>
              <h4 className="font-semibold text-xl mb-3">Advanced Analytics</h4>
              <p className="text-gray-600">
                Real-time progress tracking with predictive insights to identify learning gaps early
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-4xl mb-4">👨‍👩‍👧‍👦</div>
              <h4 className="font-semibold text-xl mb-3">Family Dashboard</h4>
              <p className="text-gray-600">
                Parents can manage multiple student accounts with detailed progress summaries
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-4xl mb-4">🔒</div>
              <h4 className="font-semibold text-xl mb-3">Secure & Safe</h4>
              <p className="text-gray-600">
                Parent-only registration with PIN-based student authentication for maximum security
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-4xl mb-4">🌐</div>
              <h4 className="font-semibold text-xl mb-3">Multi-Platform</h4>
              <p className="text-gray-600">
                Web-based platform accessible from any device with mobile-responsive design
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-4xl mb-4">⚡</div>
              <h4 className="font-semibold text-xl mb-3">Instant Feedback</h4>
              <p className="text-gray-600">
                Real-time customization based on learning patterns with immediate progress
                visualization
              </p>
            </div>
          </div>
        </div>

        {/* Problems We Solve */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-16 max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Problems with Traditional Math Education
          </h3>
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="text-red-500 text-2xl flex-shrink-0">❌</div>
              <div>
                <h4 className="font-semibold text-lg mb-1">One-Size-Fits-All Approach</h4>
                <p className="text-gray-600">
                  All students get the same worksheets regardless of skill level, leading to boredom
                  or frustration
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="text-red-500 text-2xl flex-shrink-0">❌</div>
              <div>
                <h4 className="font-semibold text-lg mb-1">Delayed Feedback</h4>
                <p className="text-gray-600">
                  Students don't know they're struggling until they fail a test - too late for
                  intervention
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="text-red-500 text-2xl flex-shrink-0">❌</div>
              <div>
                <h4 className="font-semibold text-lg mb-1">Low Engagement</h4>
                <p className="text-gray-600">
                  Static, repetitive worksheets fail to maintain interest and motivation
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="text-red-500 text-2xl flex-shrink-0">❌</div>
              <div>
                <h4 className="font-semibold text-lg mb-1">Limited Parent Involvement</h4>
                <p className="text-gray-600">
                  Parents struggle to understand progress with no clear guidance on how to help at
                  home
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison */}
        <div className="mb-16 max-w-5xl mx-auto">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Numbie vs Traditional Methods
          </h3>
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Feature
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Traditional Software
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-blue-600">
                      Numbie AI System
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">Content</td>
                    <td className="px-6 py-4 text-sm text-gray-600">Static for all students</td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">
                      ✓ Personalized for each student
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">Feedback</td>
                    <td className="px-6 py-4 text-sm text-gray-600">After mistakes</td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">
                      ✓ Predictive (before problems)
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">Difficulty</td>
                    <td className="px-6 py-4 text-sm text-gray-600">Fixed levels</td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">
                      ✓ Dynamic adjustment
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">Analytics</td>
                    <td className="px-6 py-4 text-sm text-gray-600">Basic tracking</td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">
                      ✓ Comprehensive with insights
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">User Roles</td>
                    <td className="px-6 py-4 text-sm text-gray-600">Student only</td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">
                      ✓ Multi-stakeholder platform
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-2xl p-12 text-center text-white max-w-4xl mx-auto">
          <h3 className="text-4xl font-bold mb-4">Ready to Transform Math Learning?</h3>
          <p className="text-xl mb-8">
            Join thousands of families already using Numbie to achieve better outcomes
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/signup')}
            className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6"
          >
            Get Started Free
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center mt-16 pt-8 border-t border-gray-200">
          <p className="text-gray-600 text-sm">© 2026 Numbie Learning. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Landing;
